import { useAlerts } from './useAlerts';
import toast from 'react-hot-toast';
import { useEffect, useRef } from 'react';
import type { AlertFilters } from '../api/alerts';

const STORAGE_KEY = 'alertToastsLastShown';

export function useAlertsWithToast(filters?: AlertFilters) {
  const alertsHook = useAlerts(filters);
  const hasShownInitialToasts = useRef(false);

  // Afficher des toasts pour les nouvelles alertes urgentes
  useEffect(() => {
    if (!alertsHook.loading && alertsHook.alerts.length > 0 && !hasShownInitialToasts.current) {
      hasShownInitialToasts.current = true;

      // Vérifier si les toasts ont déjà été affichés PENDANT CETTE SESSION
      const lastShown = sessionStorage.getItem(STORAGE_KEY);
      
      if (lastShown) {
        // Toasts déjà affichés dans cette session (jusqu'à fermeture du navigateur)
        return;
      }

      // Filtrer toutes les alertes actives (ACTIVE, DISMISSED ou CONFIRMED, mais pas SNOOZED ni RESOLVED)
      const activeAlerts = alertsHook.alerts.filter(
        alert => {
          const validStatuses = ['ACTIVE', 'DISMISSED', 'CONFIRMED'];
          return validStatuses.includes(alert.status);
        }
      );

      // Si des alertes existent, afficher les toasts et sauvegarder la date
      if (activeAlerts.length > 0) {
        // Afficher un toast pour chaque alerte
        activeAlerts.forEach((alert) => {
          let emoji = '📅';
          let backgroundColor = '#fff';
          let textColor = '#000';
          let message = alert.message;

          if (alert.type === 'CONTRACT_EXPIRY') {
            // Alertes de fin de contrat avec code couleur
            const daysRemaining = alert.days_remaining ?? 0;

            if (daysRemaining <= 0) {
              // 0 jours ou expiré : rouge
              emoji = '🚨';
              backgroundColor = '#ef4444';
              textColor = '#fff';
            } else if (daysRemaining <= 7) {
              // 1 à 7 jours : rouge
              emoji = '🚨';
              backgroundColor = '#ef4444';
              textColor = '#fff';
            } else if (daysRemaining <= 15) {
              // 8 à 15 jours : jaune
              emoji = '⚠️';
              backgroundColor = '#f59e0b';
              textColor = '#fff';
            } else if (daysRemaining <= 30) {
              // 16 à 30 jours : blanc
              emoji = '📅';
              backgroundColor = '#fff';
              textColor = '#000';
            } else {
              // Plus de 30 jours : blanc
              emoji = '📅';
              backgroundColor = '#fff';
              textColor = '#000';
            }

            message = alert.days_remaining !== null
              ? `${alert.employee_name} - Contrat expire dans ${alert.days_remaining} jour${alert.days_remaining > 1 ? 's' : ''}`
              : alert.message;
          } else if (alert.type === 'CUSTOM') {
            // Alertes personnalisées (rappels programmés)
            emoji = '🔔';
            backgroundColor = '#3b82f6'; // bleu
            textColor = '#fff';
            message = alert.title || alert.message;
          }

          toast(message, {
            icon: emoji,
            duration: 6000,
            style: {
              background: backgroundColor,
              color: textColor,
              border: backgroundColor === '#fff' ? '2px solid #e5e7eb' : 'none',
            },
          });
        });

        // Sauvegarder dans sessionStorage (se réinitialise à la fermeture du navigateur)
        sessionStorage.setItem(STORAGE_KEY, 'shown');
      }
    }
  }, [alertsHook.loading, alertsHook.alerts]);

  const snoozeWithToast = async (id: number, days: number = 1) => {
    try {
      await alertsHook.snooze(id, days);
      toast.success(`Alerte reportée de ${days} jour${days > 1 ? 's' : ''}`, {
        icon: '⏰',
        duration: 3000,
      });
    } catch (err) {
      toast.error('Erreur lors du report de l\'alerte', {
        icon: '❌',
        duration: 4000,
      });
      throw err;
    }
  };

  const dismissWithToast = async (id: number) => {
    try {
      await alertsHook.dismiss(id);
      toast.success('Alerte ignorée', {
        icon: '🗑️',
        duration: 3000,
      });
    } catch (err) {
      toast.error('Erreur lors de l\'ignorance de l\'alerte', {
        icon: '❌',
        duration: 4000,
      });
      throw err;
    }
  };

  const confirmWithToast = async (id: number) => {
    try {
      await alertsHook.confirm(id);
      toast.success('Alerte confirmée', {
        icon: '✅',
        duration: 3000,
      });
    } catch (err) {
      toast.error('Erreur lors de la confirmation de l\'alerte', {
        icon: '❌',
        duration: 4000,
      });
      throw err;
    }
  };

  const markAsReadWithToast = async (id: number) => {
    try {
      await alertsHook.markAsRead(id);
      toast.success('Alerte marquée comme lue', {
        icon: '👁️',
        duration: 2000,
      });
    } catch (err) {
      toast.error('Erreur lors du marquage comme lue', {
        icon: '❌',
        duration: 4000,
      });
      throw err;
    }
  };

  const markAllAsReadWithToast = async () => {
    try {
      await alertsHook.markAllAsRead();
      toast.success('Toutes les alertes ont été marquées comme lues', {
        icon: '✅',
        duration: 3000,
      });
    } catch (err) {
      toast.error('Erreur lors du marquage de toutes les alertes', {
        icon: '❌',
        duration: 4000,
      });
      throw err;
    }
  };

  return {
    ...alertsHook,
    snooze: snoozeWithToast,
    dismiss: dismissWithToast,
    confirm: confirmWithToast,
    markAsRead: markAsReadWithToast,
    markAllAsRead: markAllAsReadWithToast,
  };
}
